import { useQuery } from "@tanstack/react-query";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { useObjectConfigurationValue } from "~/contexts/configuration";
import useScript from "~/hooks/use-script";
import { ApiConstants } from "~/shared/api/api-constants.class";
import CrossCountry from "~/types/cross-country";
import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";
import contentfulUtils from "~/utils/contentful-utils";
import $fetch from "~/utils/fetch-utils";
import imageUtils from "~/utils/image-utils";

import Grid from "../common/grid";

const NextImage = dynamic(() => import("../common/next-image"));
const SkeletonTris = dynamic(() => import("../common/skeletons/skeleton-tris"));
const RevieveModalTrigger = dynamic(() => import("../editorials/revieve-modal-trigger/revieve-modal-trigger"));
import styles from "./product-beauty-services.module.scss";

type Props = {
  root: CrossCountry.DetailedProduct;
  selectedShade: CrossCountry.DetailedProduct;
};

interface TrisImage {
  url: string;
  description: string;
}

interface TrisResponse {
  tris: TrisImage[];
}

export default function ProductBeautyServices(props: Props) {
  const t = useTranslations();
  const router = useRouter();
  const contentfulLocale = contentfulUtils.mapLocale(router.locale);
  const revieveConfig = useObjectConfigurationValue("App:RevieveConfigs");
  const trisVideosConfig = useObjectConfigurationValue("App:Ecommerce:TrisVideos");
  const videoUrlForProduct = trisVideosConfig?.[props.root.backend_id];
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;

    const handleVideoEnd = () => {
      if (videoElement) {
        videoElement.load();
      }
    };

    videoElement?.addEventListener("ended", handleVideoEnd);

    return () => {
      videoElement?.removeEventListener("ended", handleVideoEnd);
    };
  }, [videoRef]);

  const { data: trisImages, isLoading } = useQuery({
    queryKey: ["trisImages", props.selectedShade.backend_id],
    staleTime: Infinity,
    queryFn: async () => {
      const queryParams = new URLSearchParams();

      if (props.root.backend_id !== props.selectedShade.backend_id) {
        queryParams.append("parentProductId", props.root.backend_id);
      }

      if (props.selectedShade.ecomm_online_category_master) {
        queryParams.append("categoryMaster", props.selectedShade.ecomm_online_category_master);
      }

      const url = `${ApiConstants.endpoints.contentfulTris}`
        .replace("{locale}", contentfulLocale)
        .replace("{sku}", props.selectedShade.backend_id);

      const queryString = queryParams.toString();
      const fullUrl = queryString ? `${url}?${queryString}` : url;

      const response = await $fetch(fullUrl).json();
      return (response as TrisResponse).tris;
    },
    enabled: !!props.root && !!props.selectedShade,
  });

  useScript(process.env.NEXT_PUBLIC_REVIEVE_API_URL);

  const hasServices =
    props.selectedShade.skin_care_diagnostic ||
    props.selectedShade.try_it_on ||
    props.selectedShade.foundation_finder_enabled;

  if (trisImages?.length === 0) {
    return null;
  }

  if (CommonUtils.parseBoolean(process.env.NEXT_PUBLIC_ENABLE_CLOUDFRONT_ASSETS) && trisImages) {
    trisImages.forEach((trisImage) => {
      trisImage.url = trisImage.url.replace(
        Constants.ASSETS_CDN_IMAGES_SOURCE.CONTENTFUL,
        Constants.ASSETS_CDN_IMAGES_SOURCE.CLOUDFRONT
      );
    });
  }

  return (
    <Grid className={styles.container}>
      {revieveConfig && hasServices && (
        <div className={styles.buttonCol}>
          <div>
            <h2>{t("generic.product_services")}</h2>
            <p>{t("generic.product_services_desc")}</p>
          </div>
          {props.selectedShade.skin_care_diagnostic && (
            <RevieveModalTrigger
              revieveType="skincare"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_skin")}
            />
          )}
          {props.selectedShade.try_it_on && (
            <RevieveModalTrigger
              revieveType="virtualTryOn"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_makeup")}
            />
          )}
          {props.selectedShade.foundation_finder_enabled && (
            <RevieveModalTrigger
              revieveType="foundationFinder"
              variant="serviceMedium"
              className={styles.revieveBtn}
              buttonText={t("generic.product_services_foundation")}
            />
          )}
        </div>
      )}
      {isLoading ? (
        <SkeletonTris hasServices={hasServices} />
      ) : (
        <div className={classNames(styles.mosaic, !hasServices && styles.noServices)}>
          {trisImages?.[2] ? (
            <div className={styles.mosaicMedia}>
              {videoUrlForProduct ? (
                <video
                  ref={videoRef}
                  src={videoUrlForProduct}
                  width="100%"
                  height="100%"
                  poster={`https:${trisImages[2].url}`}
                  muted
                  controls
                  controlsList="nodownload"
                  playsInline
                  className={styles.video}
                />
              ) : (
                <NextImage
                  src={`https:${trisImages[2].url}`}
                  alt={trisImages[2].description}
                  fill
                  loader={imageUtils.contentfulImageLoader}
                />
              )}
            </div>
          ) : (
            <div className={styles.mosaicMedia} />
          )}
          {trisImages?.[0] && (
            <div className={styles.mosaicMedia}>
              <NextImage
                src={`https:${trisImages[0].url}`}
                alt={trisImages[0].description}
                fill
                loader={imageUtils.contentfulImageLoader}
              />
            </div>
          )}
          {trisImages?.[1] && (
            <div className={styles.mosaicMedia}>
              <NextImage
                src={`https:${trisImages[1].url}`}
                alt={trisImages[1].description}
                fill
                loader={imageUtils.contentfulImageLoader}
              />
            </div>
          )}
        </div>
      )}
    </Grid>
  );
}
